import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Apis from '../../../api';
import Loader from '../../common/Loader';
import { DataRoom, DataRoomChildren } from '../deals/redux/types';
import PDFPagination from './components/PDFPagination';
import PDFLazyLoading from './components/PDFLazyLoading';
import download from 'downloadjs';

export interface PDFStateData {
    breadCrumbs: DataRoom[];
    list: DataRoom[] | DataRoomChildren[];
    dealUUID: string;
    parentUUID: string;
    docUUID: string;
    fileName: string;
    isDownloadable: boolean;
}

const PDFView: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const fileBlob = useRef<null | Blob>(null);
    const trackUUID = useRef(null);

    const stateData: PDFStateData = location.state as PDFStateData;
    const { dealUUID, parentUUID, docUUID, fileName, breadCrumbs, list, isDownloadable } = stateData;

    const [loading, setLoading] = useState(false);
    const [fileObject, setFile] = useState<string | null>(null);
    const [isVertical, setIsVertical] = useState(false);
    const [restart, setRestart] = useState(true);

    useEffect(() => {
        try {
            Apis.analyticsTrackStart(dealUUID, docUUID, 'ViewDoc').then((res) => {
                const { uuid } = res.data;
                trackUUID.current = uuid;
            });
            return () => {
                if (trackUUID.current) {
                    Apis.analyticsTrackUpdate(dealUUID, trackUUID.current);
                }
            };
        } catch (err) {}
    }, [dealUUID, docUUID]);

    useEffect(() => {
        try {
            setLoading(true);
            Apis.downloadDataRoomDocument(dealUUID, parentUUID, docUUID, 'DataRoom')
                .then((res) => {
                    let file = new Blob([res.data], { type: 'application/pdf' });
                    fileBlob.current = file;
                    let fileURL = window.URL.createObjectURL(file);
                    setFile(fileURL);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (err) {}
    }, [dealUUID, parentUUID, docUUID]);

    useEffect(() => {
        if (window.innerWidth < 900) {
            setIsVertical(true);
        }
        if (restart) {
            setRestart(false);
        }

        function handleResize() {
            setIsVertical(window.innerWidth < 900 ? true : false);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [restart]);

    const redirectToDealDetails = useCallback(() => {
        navigate(`/deal/${dealUUID}`, { state: { parentUUID, breadCrumbs, list, isDownloadable } });
    }, [dealUUID, parentUUID, breadCrumbs, list, navigate, isDownloadable]);

    /**File Download */
    const onDownload = useCallback(() => {
        if (fileBlob.current) {
            download(fileBlob.current, fileName, 'application/pdf');
            try {
                Apis.analyticsTrackStart(dealUUID, docUUID, 'DownloadDoc');
            } catch (err) {}
        }
    }, [fileName, dealUUID, docUUID]);

    /**Get PDF view according vertical */
    const pdfComponent = useMemo(() => {
        if (fileObject) {
            if (isVertical) {
                return (
                    <PDFPagination
                        fileObject={fileObject}
                        fileName={fileName}
                        redirectToDealDetails={redirectToDealDetails}
                        isDownloadable={isDownloadable}
                        onDownload={onDownload}
                    />
                );
            }
            return (
                <PDFLazyLoading
                    fileObject={fileObject}
                    fileName={fileName}
                    redirectToDealDetails={redirectToDealDetails}
                    isDownloadable={isDownloadable}
                    onDownload={onDownload}
                />
            );
        }
        return null;
    }, [isVertical, fileObject, fileName, redirectToDealDetails, isDownloadable, onDownload]);

    return (
        <div style={{ backgroundColor: '#f0f2f5' }}>
            {!loading && <div className="mt-8">{pdfComponent}</div>}
            {loading && (
                <div className="p-4 flex justify-center items-center">
                    <Loader />
                </div>
            )}
        </div>
    );
};

export default PDFView;
